"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";

type Panel = {
  title: string;
  subtitle?: string;
  catalogHref: string;
  content: ReactNode;
};

type HomeExploreGroupPanelsProps = {
  groupIndex: number;
  heading: string;
  description?: string;
  panels: Panel[];
};

export default function HomeExploreGroupPanels({
  groupIndex,
  heading,
  description: _description,
  panels,
}: HomeExploreGroupPanelsProps) {
  const t = useTranslations("Explore");
  const [sel, setSel] = useState(0);
  const n = panels.length;
  const safeIndex = Math.min(sel, Math.max(0, n - 1));
  const active = panels[safeIndex];

  if (n === 0 || !active) return null;

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col gap-2.5">
        <div className="space-y-1">
          <h3
            id={`explore-group-${groupIndex}-label`}
            className="text-base font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:text-lg"
          >
            {heading}
          </h3>
          {/* {_description && (
            <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">{_description}</p>
          )} */}
        </div>

        <div
          role="tablist"
          aria-labelledby={`explore-group-${groupIndex}-label`}
          className="flex flex-wrap gap-2"
        >
          {panels.map((p, i) => {
            const isActive = i === safeIndex;
            return (
              <button
                key={`${p.catalogHref}-${p.title}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={0}
                id={`explore-g${groupIndex}-tab-${i}`}
                aria-controls={`explore-g${groupIndex}-panel`}
                title={p.subtitle ? `${p.title} · ${p.subtitle}` : p.title}
                onClick={() => setSel(i)}
                onFocus={() => setSel(i)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                    e.preventDefault();
                    setSel(Math.min(n - 1, i + 1));
                  } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                    e.preventDefault();
                    setSel(Math.max(0, i - 1));
                  }
                }}
                className={`inline-flex max-w-[min(100%,18rem)] items-center truncate rounded-full border px-2.5 py-1 text-left text-[11px] font-semibold transition-colors sm:text-xs ${
                  isActive
                    ? "border-[#E63946] bg-[#E63946]/10 text-[#E63946] shadow-sm dark:bg-[#E63946]/15"
                    : "border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                }`}
              >
                {p.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mb-1">
        <Link
          href={active.catalogHref}
          className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-[#E63946] whitespace-nowrap"
        >
          {t("allInCatalog")} <ArrowRight size={14} />
        </Link>
      </div>

      <div
        role="tabpanel"
        id={`explore-g${groupIndex}-panel`}
        aria-labelledby={`explore-g${groupIndex}-tab-${safeIndex}`}
        className="min-h-[12rem]"
      >
        {active.content}
      </div>
    </div>
  );
}
